import { mulberry32 } from '@gazette/shared';
import { FONT_BODY, FONT_DISPLAY, FONT_NEWS, FONT_TYPE } from './palette.ts';

/** Only breakable spaces: the non-breaking ones French punctuation relies on keep « Un » whole. */
export const BREAKABLE_SPACE = /[ \t\n\r]+/;

/** Greedy word wrap; words longer than a line are broken so nothing overflows the column. */
export function wrapText(ctx: Pick<CanvasRenderingContext2D, 'measureText'>, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(BREAKABLE_SPACE).filter(Boolean)) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth) {
      line = candidate;
      continue;
    }
    if (line) lines.push(line);
    if (ctx.measureText(word).width <= maxWidth) {
      line = word;
      continue;
    }
    let chunk = '';
    for (const char of word) {
      if (ctx.measureText(chunk + char).width > maxWidth && chunk) {
        lines.push(chunk);
        chunk = '';
      }
      chunk += char;
    }
    line = chunk;
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * Wraps with a narrower measure for the first lines, where a portrait sits beside the text.
 * Works on words rather than searching for the wrapped line in the source, which breaks as
 * soon as wrapping rewrites a separator.
 */
export function wrapAround(ctx: Pick<CanvasRenderingContext2D, 'measureText'>, text: string, full: number, narrow: number, narrowLines: number): string[] {
  const lines: string[] = [];
  let words = text.split(BREAKABLE_SPACE).filter(Boolean);
  while (words.length && lines.length < narrowLines) {
    const [first] = wrapText(ctx, words.join(' '), narrow);
    if (!first) break;
    lines.push(first);
    const taken = first.split(' ');
    const lastTaken = taken.at(-1)!;
    const lastWord = words[taken.length - 1]!;
    // A word too long for the column was cut mid-word: carry its remainder to the next line.
    words = lastWord !== lastTaken && lastWord.startsWith(lastTaken)
      ? [lastWord.slice(lastTaken.length), ...words.slice(taken.length)]
      : words.slice(taken.length);
  }
  return words.length ? [...lines, ...wrapText(ctx, words.join(' '), full)] : lines;
}

/** Faint fibers and specks so flat fills read as card stock or newsprint. */
export function paperGrain(ctx: CanvasRenderingContext2D, width: number, height: number, seed: number, strength = 1): void {
  const rng = mulberry32(seed);
  ctx.save();
  const count = Math.round((width * height) / 900) * strength;
  for (let i = 0; i < count; i++) {
    ctx.globalAlpha = 0.03 + rng() * 0.05;
    ctx.fillStyle = rng() < 0.5 ? '#5a4118' : '#fff';
    const x = rng() * width;
    const y = rng() * height;
    if (rng() < 0.3) {
      ctx.fillRect(x, y, 1 + rng() * 10, 1);
    } else {
      ctx.fillRect(x, y, 1 + rng() * 1.5, 1 + rng() * 1.5);
    }
  }
  ctx.restore();
}

/** Knocks random specks out of whatever was just inked, like a worn rubber stamp. */
export function inkWear(ctx: CanvasRenderingContext2D, width: number, height: number, seed: number, amount = 0.5): void {
  const rng = mulberry32(seed);
  ctx.save();
  ctx.globalCompositeOperation = 'destination-out';
  const count = Math.round(((width * height) / 60) * amount);
  for (let i = 0; i < count; i++) {
    ctx.globalAlpha = 0.3 + rng() * 0.7;
    ctx.beginPath();
    ctx.arc(rng() * width, rng() * height, 0.5 + rng() * 1.8, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

export const fonts = {
  display: (size: number) => `${size}px ${FONT_DISPLAY}`,
  news: (size: number, weight = 700, italic = false) => `${italic ? 'italic ' : ''}${weight} ${size}px ${FONT_NEWS}`,
  type: (size: number, weight = 400) => `${weight} ${size}px ${FONT_TYPE}`,
  body: (size: number, weight = 500) => `${weight} ${size}px ${FONT_BODY}`,
};

let fontsReady: Promise<void> | null = null;

/** Canvas text silently falls back to a default face if drawn before the webfont loads. */
export function loadCanvasFonts(): Promise<void> {
  fontsReady ??= Promise.all([
    document.fonts.load(fonts.display(40)),
    document.fonts.load(fonts.news(40, 900)),
    document.fonts.load(fonts.news(40, 700)),
    document.fonts.load(fonts.news(40, 400, true)),
    document.fonts.load(fonts.type(40)),
    document.fonts.load(fonts.type(40, 700)),
    document.fonts.load(fonts.body(40)),
  ]).then(() => undefined);
  return fontsReady;
}
